import { useTenderDeadline } from "../../../../../hooks/useTenderDeadline";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CURRENCIES } from "../../../../../mock";
import { TenderDeadlineText } from "../../../../shared/TenderDeadlineText/TenderDeadlineText";
import CustomButton from "../../../../custom/custom-button/CustomButton";
import HintIcon from "../../../../shared/icons/HintIcon";
import CanceledIcon from "../../../../shared/icons/CanceledIcon";
import AviationExpeditorUpdateBidModal from "./AviationExpeditorUpdateBidModal";
import AviationExpeditorRevokeBidModal from "./AviationExpeditorRevokeBidModal";
import style from "./AviationExpeditorBidBlock.module.scss";
import RebidNotice from "../../../../orders-new/order-info/components/RebidNotice/RebidNotice";

const formatPrice = (value, currency = "EUR") => {
  if (value === null || value === undefined) return "-";
  const formatted = Number(value).toLocaleString("ru-RU").replace(/,/g, " ");
  const resolvedCurrency = currency || "EUR";
  const symbol = CURRENCIES[resolvedCurrency]?.symbol || resolvedCurrency;
  return `${formatted} ${symbol}`;
};

const AviationExpeditorBidBlock = ({
  onRefreshOrder,
  bid,
  order,
  isCanceled = false,
  canceledText = "",
  isOtherExpeditorAssigned = false,
  onUpdateData,
  onFindNewOrders,
}) => {
  const { isExpired: isTenderExpiredByServer } = useTenderDeadline(order?.tenderDateEnd);
  const { t } = useTranslation();
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);

  if (!bid) return null;

  const hasMinPrice = bid.minPrice !== null && bid.minPrice !== undefined;
  const hasPosition = bid.position !== null && bid.position !== undefined;
  const hasTotalBids = bid.totalBids !== null && bid.totalBids !== undefined;
  const isLeading = hasPosition && Number(bid.position) === 1;
  const isTenderEnded =
    Boolean(order?.tenderDateEnd) && isTenderExpiredByServer;
  const showCompetitiveStats =
    order?.assignmentMethod === "Auction" &&
    bid.isMultiCurrency !== true &&
    !isOtherExpeditorAssigned &&
    (hasMinPrice || hasPosition);
  let positionText = "";
  if (hasPosition) {
    positionText = hasTotalBids
      ? t("orderInfoExpeditor.bid.position", {
          position: bid.position,
          total: bid.totalBids,
        })
      : String(bid.position);
  }
  const isTenderClosed = isCanceled || isTenderEnded || isOtherExpeditorAssigned;

  return (
    <div className={style.card}>
      {order?.isInRebid === true && !isTenderEnded && (
        <RebidNotice>
          {t(
            order?.assignmentMethod === "Auction"
              ? "orderInfoExpeditor.bid.auctionRebidNotice"
              : "orderInfoExpeditor.bid.rebidNotice",
          )}
        </RebidNotice>
      )}

      <div className={style.topbar}>
        <div className={style.topbar_left}>
          <span className={style.your_bid_label}>{t("orderInfoExpeditor.bid.yourBid")}</span>
          <span className={style.your_bid_price}>{formatPrice(bid.price, bid.currency)}</span>
        </div>
        {order?.tenderDateEnd ? (
          <div className={style.topbar_right}>
            <span className={style.deadline_label}>
              {t(
                order?.isInRebid === true
                  ? "orderInfoExpeditor.bid.rebidClosingDeadline"
                  : order?.assignmentMethod === "Auction"
                    ? "orderInfoExpeditor.bid.auctionClosingDeadline"
                    : "orderInfoExpeditor.bid.tenderClosingDeadline",
              )}
            </span>
            <span className={style.deadline_value}>
              <TenderDeadlineText date={order.tenderDateEnd} />
            </span>
          </div>
        ) : null}
      </div>

      {showCompetitiveStats ? (
        <div className={style.stats_row}>
          {hasMinPrice ? (
            <div className={style.stat_cell}>
              <span className={style.stat_label}>{t("orderInfoExpeditor.bid.bestBid")}</span>
              <span className={style.stat_value}>{formatPrice(bid.minPrice, bid.currency)}</span>
            </div>
          ) : null}
          {hasPosition ? (
            <div className={style.stat_cell}>
              <span className={style.stat_label}>{t("orderInfoExpeditor.bid.yourPlace")}</span>
              <span className={isLeading ? style.stat_value_lead : style.stat_value_behind}>
                {positionText}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      {isCanceled ? (
        <div className={style.hint_box_danger}>
          <CanceledIcon />
          <span className={style.hint_text_danger}>{canceledText}</span>
        </div>
      ) : isOtherExpeditorAssigned ? (
        <div className={style.hint_box_danger}>
          <HintIcon color="#E5373C" />
          <span className={style.hint_text_danger}>{t(order?.assignmentMethod === "Auction"
            ? "orderInfoExpeditor.bid.auctionEnded" : "orderInfoExpeditor.bid.tenderEnded")}</span>
        </div>
      ) : isTenderEnded ? (
        <div className={style.hint_box_error}>
          <HintIcon />
          <span className={style.hint_text_error}>
            {t(order?.assignmentMethod === "Auction"
              ? "orderInfoExpeditor.bid.auctionCompletedAwaitingDecision"
              : "orderInfoExpeditor.bid.tenderCompletedAwaitingDecision")}
          </span>
        </div>
      ) : showCompetitiveStats && hasPosition && !isLeading ? (
        <div className={style.hint_box}>
          <HintIcon />
          <span className={style.hint_text}>{t("orderInfoExpeditor.bid.lowerBidHint")}</span>
        </div>
      ) : null}

      {isTenderClosed ? (
        <div className={style.buttons_row}>
          <CustomButton
            text={t("orderInfoExpeditor.bid.findNewOrders")}
            isNavy
            onClick={onFindNewOrders}
            style={{ width: "100%" }}
          />
        </div>
      ) : (
        <div className={style.buttons_row}>
          <div className={style.btn_primary}>
            <CustomButton
              text={t("orderInfoExpeditor.bid.changeBid")}
              isNavy
              onClick={() => setUpdateModalOpen(true)}
              style={{ width: "100%" }}
            />
          </div>
          <div className={style.btn_secondary}>
            <CustomButton
              text={t("orderInfoExpeditor.bid.revokeBid")}
              isNavy
              isWhite
              onClick={() => setRevokeModalOpen(true)}
              style={{ width: "100%" }}
            />
          </div>
        </div>
      )}

      <AviationExpeditorUpdateBidModal
        onRefreshOrder={onRefreshOrder}
        isOpen={updateModalOpen && !isTenderEnded}
        onClose={() => setUpdateModalOpen(false)}
        bid={bid}
        order={order}
        onSuccess={() => {
          setUpdateModalOpen(false);
          onUpdateData?.();
        }}
      />

      <AviationExpeditorRevokeBidModal
        onRefreshOrder={onRefreshOrder}
        order={order}
        isOpen={revokeModalOpen && !isTenderEnded}
        onClose={() => setRevokeModalOpen(false)}
        bid={bid}
        onSuccess={() => {
          setRevokeModalOpen(false);
          onUpdateData?.();
        }}
      />
    </div>
  );
};

export default AviationExpeditorBidBlock;
